import { useMemo, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { useExpirationCountdown } from '~/domains/common/hooks/useExpirationCountdown';

import type { MissionCardProps } from '../../types/missions.types';

// Threshold is 7 days
const EXPIRATION_PILL_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

export const useMissionCard = ({
  missionAssignment,
}: MissionCardProps) => {
  const { t } = useTranslation('promotions');
  const endsAt = missionAssignment?.mission?.endsAt;
  const progress = missionAssignment?.progress;

  // Convert expirationDate to Date object if it's a string
  const expirationDateObj = useMemo(() => {
    if (!endsAt) return null;
    return typeof endsAt === 'string' ? new Date(endsAt) : endsAt;
  }, [endsAt]);

  // Stable fallback Date so `useExpirationCountdown`'s effect (which depends on
  // targetDate identity) doesn't re-run every render when no expiration is set.
  // Without this the hook loops setState → render → new Date() → setState.
  const fallbackDateRef = useRef<Date>();
  if (!fallbackDateRef.current) fallbackDateRef.current = new Date();

  const countdownResult = useExpirationCountdown(expirationDateObj || fallbackDateRef.current);

  const timeLeft = expirationDateObj ? countdownResult.value : '';
  const hasDeadlinePassed = expirationDateObj ? countdownResult.hasDeadlinePassed : false;

  const expirationChipLabel = useMemo<string | null>(() => {
    if (!expirationDateObj || !timeLeft) return null;
    if (hasDeadlinePassed) return t('promoCard.expiration.expired');

    const remainingMs = expirationDateObj.getTime() - Date.now();
    if (remainingMs > EXPIRATION_PILL_THRESHOLD_MS) return null;

    return `${t('promoCard.expiration.expires')} ${timeLeft}`;
  }, [hasDeadlinePassed, timeLeft, expirationDateObj, t]);

  const progressWidth = useMemo(() => {
    if (!progress) return null;
    if (progress.current === 0) return '0%';
    return `${progress.current / progress.required * 100}%`;
  }, [progress]);

  return {
    expirationChipLabel,
    hasDeadlinePassed,
    progressWidth,
  };
};
